import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import {
	ApiError,
	type CastMe,
	type CastProfile,
	type CastProfileInput,
	fetchMe,
	fetchOwnProfile,
	getAccessToken,
	updateOwnProfile,
} from "@/lib/api.ts";

type State =
	| { kind: "loading" }
	| { kind: "ready"; cast: CastMe; profile: CastProfile }
	| { kind: "error"; message: string };

interface FormState {
	name: string;
	age: string;
	height: string;
	bust: string;
	waist: string;
	hip: string;
	cupSize: string;
	bloodType: string;
	selfIntroduction: string;
}

function initialFormFrom(p: CastProfile): FormState {
	return {
		name: p.name,
		age: p.age == null ? "" : String(p.age),
		height: p.height == null ? "" : String(p.height),
		bust: p.bust == null ? "" : String(p.bust),
		waist: p.waist == null ? "" : String(p.waist),
		hip: p.hip == null ? "" : String(p.hip),
		cupSize: p.cupSize ?? "",
		bloodType: p.bloodType ?? "",
		selfIntroduction: p.selfIntroduction ?? "",
	};
}

// Parse the form back into the API's payload shape. Empty strings become
// null so we clear existing values instead of sending "".
function toPayload(form: FormState): CastProfileInput {
	const optInt = (s: string): number | null => (s === "" ? null : Number.parseInt(s, 10));
	const optStr = (s: string): string | null => (s.trim() === "" ? null : s);
	return {
		name: form.name.trim(),
		age: optInt(form.age),
		height: optInt(form.height),
		bust: optInt(form.bust),
		waist: optInt(form.waist),
		hip: optInt(form.hip),
		cupSize: optStr(form.cupSize),
		bloodType: optStr(form.bloodType),
		selfIntroduction: optStr(form.selfIntroduction),
	};
}

export function Profile() {
	const [, setLocation] = useLocation();
	const [state, setState] = useState<State>({ kind: "loading" });
	const [form, setForm] = useState<FormState | null>(null);
	const [saving, setSaving] = useState(false);
	const [flash, setFlash] = useState<string | null>(null);

	useEffect(() => {
		if (!getAccessToken()) {
			setLocation("/");
			return;
		}
		let cancelled = false;
		(async () => {
			try {
				const cast = await fetchMe();
				const profile = await fetchOwnProfile(cast.id);
				if (!cancelled) {
					setState({ kind: "ready", cast, profile });
					setForm(initialFormFrom(profile));
				}
			} catch (err) {
				if (cancelled) return;
				if (err instanceof ApiError && err.status === 401) {
					setLocation("/");
					return;
				}
				setState({
					kind: "error",
					message: err instanceof Error ? err.message : "予期しないエラーが発生しました",
				});
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [setLocation]);

	if (state.kind === "loading" || form == null) {
		return (
			<main className="flex min-h-screen items-center justify-center">
				<p className="text-sm text-gray-600">読み込み中...</p>
			</main>
		);
	}

	if (state.kind === "error") {
		return (
			<main className="mx-auto max-w-md px-4 py-16">
				<div className="rounded-2xl bg-white p-6 shadow-sm">
					<h1 className="text-lg font-semibold">エラー</h1>
					<p className="mt-3 text-sm text-gray-700">{state.message}</p>
				</div>
			</main>
		);
	}

	function updateField<K extends keyof FormState>(key: K, value: FormState[K]) {
		setForm((cur) => (cur == null ? cur : { ...cur, [key]: value }));
	}

	async function onSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (state.kind !== "ready" || form == null) return;
		setSaving(true);
		try {
			const updated = await updateOwnProfile(toPayload(form));
			setState({ kind: "ready", cast: state.cast, profile: updated });
			setForm(initialFormFrom(updated));
			setFlash("保存しました");
			setTimeout(() => setFlash(null), 2000);
		} catch (err) {
			setFlash(err instanceof Error ? err.message : "保存に失敗しました");
			setTimeout(() => setFlash(null), 3000);
		} finally {
			setSaving(false);
		}
	}

	return (
		<main className="mx-auto max-w-md space-y-4 px-4 py-8">
			<header className="flex items-center justify-between">
				<div>
					<p className="text-xs text-gray-500">プロフィール</p>
					<h1 className="text-xl font-semibold">{state.cast.name} さん</h1>
				</div>
				<Link href="/shifts" className="text-xs text-gray-600 underline underline-offset-2">
					シフトへ
				</Link>
			</header>

			<form onSubmit={onSubmit} className="space-y-4 rounded-2xl bg-white p-5 shadow-sm">
				<h2 className="text-base font-semibold">自分の情報を編集</h2>

				<label className="block">
					<span className="text-xs text-gray-600">源氏名</span>
					<input
						type="text"
						value={form.name}
						onChange={(e) => updateField("name", e.target.value)}
						maxLength={100}
						className="mt-1 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
						required
					/>
				</label>

				<div className="flex gap-3">
					<label className="flex-1">
						<span className="text-xs text-gray-600">年齢</span>
						<input
							type="number"
							inputMode="numeric"
							min={0}
							max={200}
							value={form.age}
							onChange={(e) => updateField("age", e.target.value)}
							className="mt-1 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
						/>
					</label>
					<label className="flex-1">
						<span className="text-xs text-gray-600">身長 (cm)</span>
						<input
							type="number"
							inputMode="numeric"
							min={0}
							max={300}
							value={form.height}
							onChange={(e) => updateField("height", e.target.value)}
							className="mt-1 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
						/>
					</label>
				</div>

				<div className="flex gap-3">
					<label className="flex-1">
						<span className="text-xs text-gray-600">B</span>
						<input
							type="number"
							inputMode="numeric"
							min={0}
							max={300}
							value={form.bust}
							onChange={(e) => updateField("bust", e.target.value)}
							className="mt-1 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
						/>
					</label>
					<label className="flex-1">
						<span className="text-xs text-gray-600">W</span>
						<input
							type="number"
							inputMode="numeric"
							min={0}
							max={300}
							value={form.waist}
							onChange={(e) => updateField("waist", e.target.value)}
							className="mt-1 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
						/>
					</label>
					<label className="flex-1">
						<span className="text-xs text-gray-600">H</span>
						<input
							type="number"
							inputMode="numeric"
							min={0}
							max={300}
							value={form.hip}
							onChange={(e) => updateField("hip", e.target.value)}
							className="mt-1 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
						/>
					</label>
				</div>

				<div className="flex gap-3">
					<label className="flex-1">
						<span className="text-xs text-gray-600">カップ</span>
						<input
							type="text"
							maxLength={10}
							value={form.cupSize}
							onChange={(e) => updateField("cupSize", e.target.value)}
							className="mt-1 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
						/>
					</label>
					<label className="flex-1">
						<span className="text-xs text-gray-600">血液型</span>
						<select
							value={form.bloodType}
							onChange={(e) => updateField("bloodType", e.target.value)}
							className="mt-1 block w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
						>
							<option value="">選択</option>
							<option value="A">A</option>
							<option value="B">B</option>
							<option value="O">O</option>
							<option value="AB">AB</option>
						</select>
					</label>
				</div>

				<label className="block">
					<span className="text-xs text-gray-600">自己紹介</span>
					<textarea
						value={form.selfIntroduction}
						onChange={(e) => updateField("selfIntroduction", e.target.value)}
						rows={6}
						maxLength={5000}
						className="mt-1 block w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm"
					/>
				</label>

				<button
					type="submit"
					disabled={saving}
					className="w-full rounded-xl bg-green-600 py-3 text-sm font-semibold text-white disabled:opacity-50 active:bg-green-700"
				>
					{saving ? "保存中..." : "保存する"}
				</button>

				{flash ? <p className="text-center text-xs text-green-700">{flash}</p> : null}
			</form>
		</main>
	);
}
